import type { RoomAsset } from "./types";
import {
  normalizeCardinalRotationDegrees,
  resolveFurnitureFootprint,
  resolveFurnitureFootprintCellsAt,
} from "./furniture-footprint";

export const DEFAULT_SEAT_OFFSET_Y = 0.34;
export { normalizeCardinalRotationDegrees } from "./furniture-footprint";

function finiteOr(value: number | undefined, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

export function seatFacingRadiansFromRotation(
  rotation?: number
): number {
  const resolvedRotation = normalizeCardinalRotationDegrees(rotation);
  const radians = (resolvedRotation * Math.PI) / 180;
  return Math.atan2(Math.sin(radians), Math.cos(radians));
}

export function rotateSeatOffsetFromRotation(
  rotation: number | undefined,
  seatOffsetX: number | undefined,
  seatOffsetZ: number | undefined
): { x: number; z: number } {
  const resolvedRotation = normalizeCardinalRotationDegrees(rotation);
  const offsetX = clamp(finiteOr(seatOffsetX, 0), -1.5, 1.5);
  const offsetZ = clamp(finiteOr(seatOffsetZ, 0), -1.5, 1.5);
  const radians = (resolvedRotation * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  return {
    x: offsetX * cos + offsetZ * sin,
    z: -offsetX * sin + offsetZ * cos,
  };
}

export interface SeatPose {
  x: number;
  y: number;
  z: number;
  facing: number;
}

export interface SeatPoseSlot extends SeatPose {
  seatIndex: number;
  seatCellX: number;
  seatCellZ: number;
}

function seatGridCells(
  x: number | undefined,
  z: number | undefined,
  blockWidth: number | undefined,
  blockDepth: number | undefined,
  rotation: number | undefined
): Array<{ x: number; z: number }> {
  const footprint = resolveFurnitureFootprint(blockWidth, blockDepth, rotation);
  const cells = resolveFurnitureFootprintCellsAt(
    finiteOr(x, 0),
    finiteOr(z, 0),
    footprint.width,
    footprint.depth
  );
  return cells.map(([cellX, cellZ]) => ({ x: cellX, z: cellZ }));
}

export function resolveSeatPoses(
  asset: Pick<
    RoomAsset,
    | "type"
    | "x"
    | "y"
    | "z"
    | "rotation"
    | "sittable"
    | "blockWidth"
    | "blockDepth"
    | "seatOffsetX"
    | "seatOffsetY"
    | "seatOffsetZ"
  >,
  floorHeightAtCell: (x: number, z: number) => number
): SeatPoseSlot[] {
  if (asset.type !== "glb" || !asset.sittable) {
    return [];
  }
  const globalOffsetX = clamp(finiteOr(asset.seatOffsetX, 0), -1.5, 1.5);
  const globalOffsetZ = clamp(finiteOr(asset.seatOffsetZ, 0), -1.5, 1.5);
  const rotatedGlobalOffset = rotateSeatOffsetFromRotation(
    asset.rotation,
    globalOffsetX,
    globalOffsetZ
  );
  const assetLiftY = clamp(finiteOr(asset.y, 0), -0.1, 4);
  const seatLiftY = clamp(
    finiteOr(asset.seatOffsetY, DEFAULT_SEAT_OFFSET_Y),
    0.05,
    1.2
  );
  const facing = seatFacingRadiansFromRotation(asset.rotation);
  const cells = seatGridCells(
    asset.x,
    asset.z,
    asset.blockWidth,
    asset.blockDepth,
    asset.rotation
  );
  return cells.map((seatCell, seatIndex) => {
    const x = seatCell.x + rotatedGlobalOffset.x;
    const z = seatCell.z + rotatedGlobalOffset.z;
    const floorY = floorHeightAtCell(seatCell.x, seatCell.z);
    return {
      seatIndex,
      seatCellX: seatCell.x,
      seatCellZ: seatCell.z,
      x,
      y: floorY + assetLiftY + seatLiftY,
      z,
      facing,
    };
  });
}

export function resolveSeatPoseAtIndex(
  asset: Pick<
    RoomAsset,
    | "type"
    | "x"
    | "y"
    | "z"
    | "rotation"
    | "sittable"
    | "blockWidth"
    | "blockDepth"
    | "seatOffsetX"
    | "seatOffsetY"
    | "seatOffsetZ"
  >,
  floorHeightAtCell: (x: number, z: number) => number,
  seatIndex: number
): SeatPoseSlot | null {
  const poses = resolveSeatPoses(asset, floorHeightAtCell);
  return poses.find((slot) => slot.seatIndex === seatIndex) ?? null;
}

export function resolveSeatPose(
  asset: Pick<
    RoomAsset,
    | "type"
    | "x"
    | "y"
    | "z"
    | "rotation"
    | "sittable"
    | "blockWidth"
    | "blockDepth"
    | "seatOffsetX"
    | "seatOffsetY"
    | "seatOffsetZ"
  >,
  seatCellFloorY: number
): SeatPose | null {
  const poses = resolveSeatPoses(asset, () => seatCellFloorY);
  if (poses.length === 0) {
    return null;
  }
  const first = poses[0];
  return {
    x: first.x,
    y: first.y,
    z: first.z,
    facing: first.facing,
  };
}
